// Admin-only team management. Lists members and pending invites, lets
// admins promote/demote, remove, invite, and cancel invites. Wraps each
// callable's failure mode and surfaces server-side error messages
// verbatim — they're already user-friendly (HttpsError messages we set
// in the Cloud Functions).
//
// Real-time updates: members and invites are read via onSnapshot so role
// changes and invite cancellations from other tabs reflect immediately.

import { useEffect, useState } from 'react';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { ChevronUp, ChevronDown, UserMinus, UserPlus, X, Mail, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { orgMembersCol, orgPendingInvitesCol } from '../lib/firestore';
import {
  cancelInvite,
  inviteMember,
  removeMember,
  setMemberRole,
} from '../lib/orgs';
import type { OrgMember, PendingInvite, Role } from '../lib/types';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Container,
  Input,
  PageHeader,
  Row,
  Spinner,
  Stack,
  Table,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';

export default function SettingsTeamPage() {
  const { user } = useAuth();
  const { orgId } = useOrg();

  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [invites, setInvites] = useState<PendingInvite[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('member');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  // Tracks which member the admin clicked Remove on. Holds the full
  // member record so the confirm dialog can show their name.
  const [memberToRemove, setMemberToRemove] = useState<OrgMember | null>(null);

  useEffect(() => {
    if (!orgId) return;
    const unsubMembers = onSnapshot(
      query(orgMembersCol(orgId), orderBy('joinedAt', 'asc')),
      (snap) => {
        setMembers(
          snap.docs.map((d) => {
            const raw = d.data() as Omit<OrgMember, 'joinedAt'> & {
              joinedAt: number | { toMillis(): number };
            };
            return {
              ...raw,
              joinedAt:
                typeof raw.joinedAt === 'number' ? raw.joinedAt : raw.joinedAt.toMillis(),
            };
          }),
        );
      },
      (err) => {
        console.error('[team] members snapshot failed', err);
        setError('Could not load members.');
      },
    );
    const unsubInvites = onSnapshot(
      query(orgPendingInvitesCol(orgId), orderBy('createdAt', 'asc')),
      (snap) => {
        setInvites(
          snap.docs.map((d) => {
            const raw = d.data() as Omit<PendingInvite, 'createdAt'> & {
              createdAt: number | { toMillis(): number };
            };
            return {
              ...raw,
              createdAt:
                typeof raw.createdAt === 'number'
                  ? raw.createdAt
                  : raw.createdAt.toMillis(),
            };
          }),
        );
      },
      (err) => {
        console.error('[team] invites snapshot failed', err);
      },
    );
    return () => {
      unsubMembers();
      unsubInvites();
    };
  }, [orgId]);

  if (!orgId || !user) return null;

  async function withBusy<T>(key: string, fn: () => Promise<T>): Promise<T | undefined> {
    if (busy) return undefined;
    setBusy(key);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      console.error(`[team] ${key} failed`, err);
      setError(extractMessage(err));
      return undefined;
    } finally {
      setBusy(null);
    }
  }

  async function handlePromote(userId: string) {
    await withBusy(`promote:${userId}`, () => setMemberRole(orgId!, userId, 'admin'));
  }
  async function handleDemote(userId: string) {
    await withBusy(`demote:${userId}`, () => setMemberRole(orgId!, userId, 'member'));
  }
  async function handleRemoveConfirmed(userId: string) {
    const ok = await withBusy(`remove:${userId}`, () => removeMember(orgId!, userId));
    // Close the dialog only on success — on failure, withBusy has surfaced
    // the error and we keep it open so the admin can retry or cancel.
    if (ok !== undefined) setMemberToRemove(null);
  }
  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    const result = await withBusy('invite', async () => {
      const inviteId = await inviteMember(orgId!, inviteEmail.trim(), inviteRole);
      const url = `${window.location.origin}/accept-invite?orgId=${encodeURIComponent(orgId!)}&inviteId=${encodeURIComponent(inviteId)}`;
      setInviteUrl(url);
      setInviteEmail('');
      return url;
    });
    if (!result) setInviteUrl(null);
  }
  async function handleCancelInvite(inviteId: string) {
    await withBusy(`cancel:${inviteId}`, () => cancelInvite(orgId!, inviteId));
  }

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title="Team"
          subtitle="Manage members and pending invites for your org."
        />

        {error && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <ErrorBanner message={error} />
          </div>
        )}

        <Stack gap={8}>
          {/* Members */}
          <Stack gap={3}>
            <Row gap={2} align="center" justify="between">
              <Row gap={2} align="center">
                <h2 style={{ fontSize: 'var(--fs-lg)' }}>Members</h2>
                {members && (
                  <Badge tone="neutral">{members.length}</Badge>
                )}
              </Row>
            </Row>
            {members === null ? (
              <Row gap={2} align="center">
                <Spinner size="sm" />
                <span className="ds-text-muted ds-text-sm">Loading…</span>
              </Row>
            ) : members.length === 0 ? (
              <Card>
                <CardBody>
                  <p className="ds-text-muted">No members yet.</p>
                </CardBody>
              </Card>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const isSelf = m.userId === user.uid;
                    const displayName = m.displayName?.trim() || (
                      <span className="ds-text-tertiary">(no name set)</span>
                    );
                    const displayEmail = m.email || (
                      <span className="ds-table__mono">uid:{m.userId.slice(0, 8)}…</span>
                    );
                    return (
                      <tr key={m.userId}>
                        <td>
                          <Row gap={2} align="center">
                            <span>{displayName}</span>
                            {isSelf && <Badge tone="accent">you</Badge>}
                          </Row>
                        </td>
                        <td className="ds-table__mono">{displayEmail}</td>
                        <td>
                          <Badge tone={m.role === 'admin' ? 'accent' : 'neutral'} dot>
                            {m.role}
                          </Badge>
                        </td>
                        <td className="ds-table__mono">{formatDate(m.joinedAt)}</td>
                        <td style={{ textAlign: 'right' }}>
                          {isSelf ? (
                            <span className="ds-text-tertiary">—</span>
                          ) : (
                            <Row gap={1} justify="end">
                              {m.role === 'member' && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  iconLeft={<ChevronUp size={12} strokeWidth={2} />}
                                  onClick={() => handlePromote(m.userId)}
                                  disabled={!!busy}
                                  loading={busy === `promote:${m.userId}`}
                                >
                                  Promote
                                </Button>
                              )}
                              {m.role === 'admin' && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  iconLeft={<ChevronDown size={12} strokeWidth={2} />}
                                  onClick={() => handleDemote(m.userId)}
                                  disabled={!!busy}
                                  loading={busy === `demote:${m.userId}`}
                                >
                                  Demote
                                </Button>
                              )}
                              <Button
                                variant="danger-ghost"
                                size="sm"
                                iconLeft={<UserMinus size={12} strokeWidth={2} />}
                                onClick={() => setMemberToRemove(m)}
                                disabled={!!busy}
                              >
                                Remove
                              </Button>
                            </Row>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Stack>

          {/* Invite a member */}
          <Card>
            <CardHeader>
              <Row gap={2} align="center">
                <UserPlus size={16} strokeWidth={1.75} color="var(--color-accent)" />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
                  Invite a member
                </h2>
              </Row>
            </CardHeader>
            <CardBody>
              <Stack gap={4} as="form" onSubmit={handleInvite}>
                <Row gap={2} align="end" className="ds-stack--gap-2">
                  <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                    <Input
                      type="email"
                      placeholder="invitee@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                    />
                  </div>
                  <select
                    className="ds-input"
                    style={{ width: 120 }}
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as Role)}
                    disabled={!!busy}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!!busy || !inviteEmail.trim()}
                    loading={busy === 'invite'}
                    iconLeft={busy === 'invite' ? undefined : <Send size={14} strokeWidth={2} />}
                  >
                    Create invite
                  </Button>
                </Row>
                {inviteUrl && (
                  <div
                    style={{
                      background: 'var(--color-accent-soft)',
                      border: '1px solid rgba(124, 92, 255, 0.2)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-3) var(--space-4)',
                    }}
                  >
                    <Stack gap={2}>
                      <Row gap={2} align="center">
                        <Mail size={14} strokeWidth={2} color="var(--color-accent-soft-text)" />
                        <span
                          className="ds-text-sm"
                          style={{ color: 'var(--color-accent-soft-text)' }}
                        >
                          Share this link with the invitee
                        </span>
                      </Row>
                      <code
                        className="ds-mono"
                        style={{
                          wordBreak: 'break-all',
                          background: 'rgba(0, 0, 0, 0.2)',
                          padding: 'var(--space-2) var(--space-3)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--color-text)',
                          display: 'block',
                        }}
                      >
                        {inviteUrl}
                      </code>
                    </Stack>
                  </div>
                )}
              </Stack>
            </CardBody>
          </Card>

          {/* Pending invites */}
          <Stack gap={3}>
            <Row gap={2} align="center">
              <h2 style={{ fontSize: 'var(--fs-lg)' }}>Pending invites</h2>
              {invites && invites.length > 0 && <Badge tone="neutral">{invites.length}</Badge>}
            </Row>
            {invites === null ? (
              <Row gap={2} align="center">
                <Spinner size="sm" />
                <span className="ds-text-muted ds-text-sm">Loading…</span>
              </Row>
            ) : invites.length === 0 ? (
              <Card>
                <CardBody>
                  <p className="ds-text-muted">No pending invites.</p>
                </CardBody>
              </Card>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Sent</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <tr key={inv.id}>
                      <td className="ds-table__mono">{inv.email}</td>
                      <td>
                        <Badge tone={inv.role === 'admin' ? 'accent' : 'neutral'} dot>
                          {inv.role}
                        </Badge>
                      </td>
                      <td className="ds-table__mono">{formatDate(inv.createdAt)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconLeft={<X size={12} strokeWidth={2} />}
                          onClick={() => handleCancelInvite(inv.id)}
                          disabled={!!busy}
                          loading={busy === `cancel:${inv.id}`}
                        >
                          Cancel
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Stack>
        </Stack>

        <ConfirmDialog
          open={memberToRemove !== null}
          onConfirm={() => memberToRemove && handleRemoveConfirmed(memberToRemove.userId)}
          onCancel={() => setMemberToRemove(null)}
          title="Remove member?"
          confirmLabel="Remove from org"
          destructive
          loading={busy === `remove:${memberToRemove?.userId ?? ''}`}
        >
          <p>
            <strong>
              {memberToRemove?.displayName?.trim() || memberToRemove?.email || 'This member'}
            </strong>{' '}
            will lose access to your org immediately. They can be re-invited if needed.
          </p>
        </ConfirmDialog>
      </Container>
    </AppShell>
  );
}

function formatDate(ts: number): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Something went wrong. Try again.';
}
